import React from "react";
import {
  Card,
  CardBody,
  Input,
  Button,
  Divider,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Chip,
  Checkbox,
  useDisclosure
} from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useAuth } from '../contexts/auth-context';
import { addToast } from '@heroui/react';
import { updateUserProfile as userServiceUpdate } from '../services/user-service';
import { useUserData } from '../hooks/use-user-data';

interface WishlistItem {
  id: number;
  name: string;
  category: string;
  price: number;
  image: string;
}

interface Recommendation {
  id: number;
  name: string;
  category: string;
  price: number;
  image: string;
  reason: string;
}

interface Promotion {
  id: number;
  title: string;
  description: string;
  code: string;
  expiryDate: string;
  discount: number;
}

interface AddressCardProps {
  address: Address;
  onEdit: (address: Address) => void;
  onDelete: (id: number) => void;
  onSetDefault: (id: number) => void;
}

const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  onSetDefault
}) => {
  return (
    <Card className="border border-divider" disableRipple>
      <CardBody className="p-6">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{address.title}</h3>
              {address.isDefault && (
                <Badge color="primary" variant="flat" size="sm">
                  По умолчанию
                </Badge>
              )}
            </div>
            {/* Prefer a proper fullAddress when available; fallback to constructed string from parts or the title */}
            {(() => {
              const hasFull = address.fullAddress && String(address.fullAddress).trim().length > 0;
              const isSameAsTitle = hasFull && String(address.fullAddress).trim() === String(address.title || '').trim();
              const constructed = [address.city, address.street, address.house, address.apartment].filter(Boolean).join(', ');
              const display = (hasFull && !isSameAsTitle) ? address.fullAddress : (constructed || address.fullAddress || address.title);
              return <p className="text-foreground/80">{display}</p>;
            })()}
          </div>
          <div className="flex gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(address)}
            >
              <Icon icon="lucide:edit" width={16} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              isDisabled={address.isDefault}
              onPress={() => onDelete(address.id)}
            >
              <Icon icon="lucide:trash" width={16} />
            </Button>
          </div>
        </div>
        
        {!address.isDefault && (
          <div className="mt-4">
            <Button
              size="sm"
              variant="flat"
              color="primary"
              onPress={() => onSetDefault(address.id)}
            >
              Сделать основным
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export const AccountPage: React.FC<AccountPageProps> = ({ activeSection, onSectionChange }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { user, logout, isAuthenticated } = useAuth();

  const {
    addresses: userAddresses,
    orders: userOrders,
    selectedOrder: userSelectedOrder,
    loyaltyData: userLoyaltyData,
    wishlistItems: userWishlistItems,
    recommendations: userRecommendations,
    promotions: userPromotions,
    fetchAddresses,
    saveAddress,
    removeAddress,
    makeAddressDefault,
    fetchOrders,
    fetchOrderDetails,
    handleCancelOrder,
    updatePassword,
    fetchLoyaltyData,
    fetchWishlist,
    handleRemoveFromWishlist,
    fetchRecommendations,
    fetchPromotions,
    setSelectedOrder: setUserSelectedOrder,
    isLoadingAddresses,
    isLoadingOrders,
    isLoadingLoyalty,
    isLoadingWishlist,
    isLoadingRecommendations,
    isLoadingPromotions,
  } = useUserData();

  // Add reviews section to navigation menu
  const menuItems = [
    { key: "profile", label: "Мои данные", icon: <Icon icon="lucide:user" width={20} /> },
    // { key: "addresses", label: "Мои адреса", icon: <Icon icon="lucide:map-pin" width={20} /> },
    // { key: "loyalty", label: "Мои баллы", icon: <Icon icon="lucide:gift" width={20} /> },
    { key: "orders", label: "Мои заказы", icon: <Icon icon="lucide:shopping-bag" width={20} /> },
    // { key: "reviews", label: "Мои отзывы", icon: <Icon icon="lucide:message-square" width={20} /> },
    // { key: "wishlist", label: "Избранное", icon: <Icon icon="lucide:heart" width={20} /> },
    // { key: "recommendations", label: "Рекомендации", icon: <Icon icon="lucide:sparkles" width={20} /> },
    { key: "security", label: "Безопасность", icon: <Icon icon="lucide:shield" width={20} /> },
    // { key: "promotions", label: "Персональные акции", icon: <Icon icon="lucide:ticket" width={20} /> }
  ];

  // Modal states using useDisclosure
  const { isOpen: isEditProfileOpen, onOpen: onEditProfileOpen, onClose: onEditProfileClose } = useDisclosure();
  const { isOpen: isAddressModalOpen, onOpen: onAddressModalOpen, onClose: onAddressModalClose } = useDisclosure();
  const { isOpen: isPasswordModalOpen, onOpen: onPasswordModalOpen, onClose: onPasswordModalClose } = useDisclosure();
  
  // Current address being edited
  const [currentAddress, setCurrentAddress] = React.useState<Address | null>(null);
  // Local address form used in modal (partial to allow new address without id)
  const [addressForm, setAddressForm] = React.useState<Partial<Address>>({});
  // Portal container ref for address autocomplete dropdown inside modal
  const autocompletePortalRef = React.useRef<HTMLDivElement | null>(null);

  // Helper: parse suggestion or free-text address into structured fields
  const parseAddressSuggestion = (s: any) => {
    const raw = s?.raw || s?._raw || s?.data || s || {};
    const data = raw?.data || raw || {};

    // Prefer structured data from backend (DaData or similar)
    // Support multiple possible field names returned by different proxies
  const houseFromData = data.house || data.house_number || data.house_with_type || data.house_with_type_full || data.house_type || '';
  const flatFromData = data.flat || data.flat_number || data.flat_with_type || data.flat_with_type_full || data.flat_type || data.flat_type_full || '';
  const cityFromData = data.city || data.settlement || data.city_with_type || data.region || '';
  const streetFromData = data.street || data.street_with_type || data.street_with_type_full || '';
  const postalFromData = data.postal_code || data.postcode || data.postal || '';

    if (data && (cityFromData || streetFromData || houseFromData || flatFromData || postalFromData)) {
      return {
        label: s.value || data.formatted || '',
        fullAddress: s.value || data.formatted || '',
        city: cityFromData || '',
        street: streetFromData || '',
        house: houseFromData || '',
        apartment: flatFromData || '',
        zipCode: postalFromData || ''
      };
    }

  // Check top-level suggestion fields (some providers put house/flat at root)
  const topHouse = s?.house || s?.house_number || s?.house_with_type || s?.houseType || '';
  const topFlat = s?.flat || s?.flat_number || s?.flat_with_type || s?.apartment || s?.flatType || '';

  // Fallback: naive parsing from free-text value
  const v = (s && s.value) ? s.value : (typeof s === 'string' ? s : '');
    const result: any = { label: v, fullAddress: v, city: '', street: '', house: '', apartment: '', zipCode: '' };

  // If top-level fields exist, prefer them
  if (topHouse) result.house = topHouse;
  if (topFlat) result.apartment = topFlat;

    if (!v) return result;

      // apartment: look for kv/кв/apt/офис patterns and capture words or numbers
      const aptMatch = v.match(/(?:\b(?:кв(?:\.|арт(?:и?р[а-я]*)?)|к\.?|apt\.?|flat|квартира|офис|оф|office)\b)\s*[:\.\-]?\s*([\w\-\s\u0400-\u04FF]+)/i);
      if (aptMatch) {
        // trim trailing words like "подъезд" or punctuation
        result.apartment = aptMatch[1].trim().replace(/[.,;]$/, '');
      } else {
        // fallback: look for patterns like "кв 12" or "кв.12" anywhere
        const aptFallback = v.match(/\bкв\.?\s*(\d+[A-Za-zА-Яа-я\-/]*)\b/i);
        if (aptFallback) result.apartment = aptFallback[1];
      }

    // Try to detect street. Support both "Name + type" (e.g. "Ленинский пр-кт")
    // and "type + Name" (e.g. "ул Ленина"). Also support common abbreviations like pr-kt/pr-t.
    const streetTypes = '(?:ул(?:ица)?|проспект|пр-?кт|пр-?т|пер(?:\\.|ек)?|пл(?:\\.|ощ)?|шоссе|бул(?:ьвар)?|наб(?:ережная)?|проезд|тракт)';

    // 1) name followed by type: "Ленинский пр-кт"
    const nameThenTypeRegex = new RegExp('([A-Za-zА-Яа-яЁё0-9\-\.\s]+?)\\s+' + streetTypes + '\\b', 'iu');
    const nameThenTypeMatch = v.match(nameThenTypeRegex);

    // 2) type followed by name: "ул Ленина"
    const typeThenNameRegex = new RegExp(streetTypes + '\\s+([A-Za-zА-Яа-яЁё0-9\-\.\s]+)', 'iu');
    const typeThenNameMatch = v.match(typeThenNameRegex);

    let streetMatchedText = '';
    let after = '';

    if (nameThenTypeMatch) {
      // match[0] contains full "Name + type" segment
      streetMatchedText = nameThenTypeMatch[0].trim();
      result.street = streetMatchedText;
      after = v.slice((nameThenTypeMatch.index || 0) + nameThenTypeMatch[0].length);
    } else if (typeThenNameMatch) {
      // type then name: take whole matched substring as street
      streetMatchedText = typeThenNameMatch[0].trim();
      // prefer to keep both type and name as street for display
      result.street = streetMatchedText;
      after = v.slice((typeThenNameMatch.index || 0) + typeThenNameMatch[0].length);
    }

    if (streetMatchedText) {
      // try find house number and apartment in the remainder
      const houseMatch = after.match(/(?:,|)\s*(?:д(?:ом)?\.?|д\.?|house|дом)\s*[:\.]?\s*([^,;\s]+)/i) || after.match(/\b(\d+[A-Za-z\-\/А-Яа-я\u0400-\u04FF]*)\b/);
      if (houseMatch) result.house = houseMatch[1];

      // apartment in the "after" segment
      const aptAfter = after.match(/(?:\b(?:кв(?:\.|арт(?:и?р[а-я]*)?)|к\.?|apt\.?|flat|квартира|офис|оф|office)\b)\s*[:\.\-]?\s*([^,;\s]+)/i);
      if (aptAfter) {
        result.apartment = aptAfter[1].trim().replace(/[.,;]$/,'');
      } else {
        // fallback: look for 'кв 123' anywhere in the remaining text
        const aptFallback2 = after.match(/\bкв\.?\s*(\d+[A-Za-zА-Яа-я\-/]*)\b/i);
        if (aptFallback2) result.apartment = aptFallback2[1];
      }

      // city maybe before street
      const before = v.slice(0, (nameThenTypeMatch?.index ?? typeThenNameMatch?.index) || 0);
      const cityCandidate = before.split(',')[0]?.trim();
      if (cityCandidate) result.city = cityCandidate;

      return result;
    }

    // If value has commas, try split: [city, street+house, extra]
    const parts = v.split(',').map((p: string) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      result.city = parts[0];
      // try extract house number from second part
      const hh = parts[1].match(/(.*)\s+([^\s,;]+)$/);
      if (hh) {
        result.street = hh[1].trim();
        result.house = hh[2];
      } else {
        // otherwise full second part as street
        result.street = parts[1];
      }
      // apartment could be in third part — accept words or numbers
      if (parts[2]) {
        const apt = parts[2].match(/(?:\b(?:кв(?:\.|арт(?:и?р[а-я]*)?)|к\.?|apt\.?|flat|офис|оф|office)\b)\s*[:\.]?\s*([^,;]+)/i);
        if (apt) result.apartment = apt[1].trim();
      }
      return result;
    }

    // Last fallback: try split street+house at last number/word token
    const lastMatch = v.match(/(.+?)\s+([^,;\s]+)$/);
    if (lastMatch) {
      result.street = lastMatch[1].trim();
      result.house = lastMatch[2];
      return result;
    }

    // nothing parsed: set fullAddress as street
    result.street = v;
    return result;
  };

  // Extended user data state
  const [userData, setUserData] = React.useState({
    firstName: "Анна",
    lastName: "Иванова",
    email: "anna@example.com",
    phone: "+7 (999) 123-45-67",
    birthDate: "1990-05-15",
    gender: "female"
  });

  // Password change state
  const [passwordData, setPasswordData] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // Form validation state
  const [formErrors, setFormErrors] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // Fetch data when active section changes
  React.useEffect(() => {
    // Load data based on active section
    switch (activeSection) {
      case "addresses":
        // If we already have addresses from profile, avoid extra request
        if (!userAddresses || userAddresses.length === 0) fetchAddresses();
        break;
      case "loyalty":
        fetchLoyaltyData();
        break;
      case "orders":
        fetchOrders();
        break;
      case "wishlist":
        fetchWishlist();
        break;
      case "recommendations":
        fetchRecommendations();
        break;
      case "promotions":
        fetchPromotions();
        break;
      default:
        break;
    }
  }, [activeSection, fetchAddresses, fetchLoyaltyData, fetchOrders, fetchWishlist, fetchRecommendations, fetchPromotions]);

  // Handle profile update
  const handleUpdateProfile = () => {
    // Validate form
    let hasErrors = false;
    const errors = { ...formErrors };
    
    if (!userData.firstName.trim()) {
      errors.firstName = "Имя обязательно для заполнения";
      hasErrors = true;
    } else {
      errors.firstName = "";
    }
    
    if (!userData.lastName.trim()) {
      errors.lastName = "Фамилия обязательна для заполнения";
      hasErrors = true;
    } else {
      errors.lastName = "";
    }
    
    if (!userData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      errors.email = "Введите корректный email";
      hasErrors = true;
    } else {
      errors.email = "";
    }
    
    if (!userData.phone.trim() || !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(userData.phone)) {
      errors.phone = "Введите корректный номер телефона";
      hasErrors = true;
    } else {
      errors.phone = "";
    }
    
    setFormErrors(errors);
    
    if (!hasErrors) {
      // Save user data
      onEditProfileClose();
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    // Validate password form
    let hasErrors = false;
    const errors = { ...formErrors };
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = "Введите текущий пароль";
      hasErrors = true;
    } else {
      errors.currentPassword = "";
    }
    
    if (!passwordData.newPassword || passwordData.newPassword.length < 8) {
      errors.newPassword = "Пароль должен содержать минимум 8 символов";
      hasErrors = true;
    } else {
      errors.newPassword = "";
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Пароли не совпадают";
      hasErrors = true;
    } else {
      errors.confirmPassword = "";
    }
    
    setFormErrors(errors);
    
    if (!hasErrors) {
      // Call backend to change password
      try {
        const res: any = await updatePassword(passwordData.currentPassword, passwordData.newPassword, passwordData.confirmPassword);

        if (res?.success) {
          // Closed by hook toast already; close modal and reset
          onPasswordModalClose();
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setFormErrors({ ...formErrors, currentPassword: '', newPassword: '', confirmPassword: '' });
        } else if (res?.errors) {
          // Map server validation errors to local formErrors
          const serverErrors: any = res.errors;
          const newErrors = { ...errors };
          if (serverErrors.current_password) newErrors.currentPassword = Array.isArray(serverErrors.current_password) ? serverErrors.current_password[0] : String(serverErrors.current_password);
          if (serverErrors.new_password) newErrors.newPassword = Array.isArray(serverErrors.new_password) ? serverErrors.new_password[0] : String(serverErrors.new_password);
          if (serverErrors.new_password_confirm) newErrors.confirmPassword = Array.isArray(serverErrors.new_password_confirm) ? serverErrors.new_password_confirm[0] : String(serverErrors.new_password_confirm);
          setFormErrors(newErrors);
        } else {
          // Generic failure (toast already shown by hook)
        }
      } catch (err) {
        console.error('Unhandled error changing password:', err);
      }
    }
  };

  // Handle address form submission (uses saveAddress from hook)
  const handleAddressSubmit = async () => {
    // prepare payload in backend API format
    const payload: any = {
      label: (addressForm.title || addressForm.label) || 'Адрес',
      city: addressForm.city || '',
      street: addressForm.street || '',
      house: addressForm.house || '',
      apartment: addressForm.apartment || '',
      floor: addressForm.floor || '',
      intercom: addressForm.intercom || '',
      private_house: !!addressForm.private_house,
      is_primary: !!addressForm.isDefault
    };

    try {
      if (addressForm && typeof addressForm.id === 'number' && addressForm.id > 0) {
        // Include id for update if backend expects it in payload
        payload.id = addressForm.id;
        await saveAddress(payload as any);
      } else {
        // Create new address
        const created = await saveAddress(payload as any);
        // If saveAddress returned null (failure), fallback to refetch addresses
        if (!created) {
          await fetchAddresses();
        }
      }
      // Note: if saveAddress returned the created/updated address it already updated local state
    } catch (err) {
      console.error('Error saving address from modal:', err);
    } finally {
      onAddressModalClose();
      setCurrentAddress(null);
      setAddressForm({});
    }
  };

  // Handle setting default address (use hook action)
  const handleSetDefaultAddress = async (id: number) => {
    try {
      await makeAddressDefault(id);
      // Optionally refresh
      await fetchAddresses();
    } catch (err) {
      console.error('Error setting default address:', err);
    }
  };

  // Handle address deletion (use hook action)
  const handleDeleteAddress = async (id: number) => {
    try {
      await removeAddress(id);
      await fetchAddresses();
    } catch (err) {
      console.error('Error deleting address:', err);
    }
  };

  // Handle address editing
  const handleEditAddress = (address: Address) => {
    setCurrentAddress(address);
    setAddressForm(address);
    onAddressModalOpen();
  };

  // Handle adding new address
  const handleAddAddress = () => {
    setCurrentAddress(null);
    setAddressForm({ title: '', fullAddress: '', isDefault: false, city: '', street: '', house: '', apartment: '', floor: '', intercom: '', private_house: false });
    onAddressModalOpen();
  };

  // Navigation helpers
  const getSectionIcon = (section: string) => {
    switch (section) {
      case "profile": return <Icon icon="lucide:user" width={20} />;
      case "addresses": return <Icon icon="lucide:map-pin" width={20} />;
      case "loyalty": return <Icon icon="lucide:gift" width={20} />;
      case "orders": return <Icon icon="lucide:shopping-bag" width={20} />;
      case "wishlist": return <Icon icon="lucide:heart" width={20} />;
      case "reviews": return <Icon icon="lucide:message-square" width={20} />; // New section
      case "recommendations": return <Icon icon="lucide:sparkles" width={20} />;
      case "security": return <Icon icon="lucide:shield" width={20} />;
      case "promotions": return <Icon icon="lucide:ticket" width={20} />;
      default: return null;
    }
  };

  const getSectionTitle = (section: string) => {
    switch (section) {
      case "profile": return "Мои данные";
      case "addresses": return "Мои адреса";
      // case "loyalty": return "Мои баллы";
      case "orders": return "Мои заказы";
      // case "wishlist": return "Избранное";
      // case "reviews": return "Мои отзывы"; // New section
      // case "recommendations": return "Рекомендации";
      case "security": return "Безопасность";
      // case "promotions": return "Персональные акции";
      default: return "";
    }
  };

  // Render mobile menu/navigation
  const renderMobileMenu = () => (
    <div className="lg:hidden mb-6">
      <Button
        variant="flat"
        color="default"
        className="w-full flex justify-between items-center mb-4"
        endContent={<Icon icon={isMobileMenuOpen ? "lucide:chevron-up" : "lucide:chevron-down"} width={20} />}
        onPress={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {menuItems.find(item => item.key === activeSection)?.label || "Личный кабинет"}
      </Button>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="mb-6">
              <CardBody className="p-0">
                <ul>
                  {menuItems.map((item) => (
                    <li key={item.key}>
                      <Button
                        variant="light"
                        className={`w-full justify-start rounded-none text-foreground ${activeSection === item.key ? 'bg-primary/10 text-primary' : ''}`}
                        onPress={() => {
                          onSectionChange(item.key);
                          setIsMobileMenuOpen(false);
                        }}
                        startContent={item.icon}
                      >
                        {item.label}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Animation variants for page transitions
  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  // Render section content with animation
  const renderSectionContent = (section: string) => {
    switch (section) {
      case 'profile':
        return <ProfileSection />;
      case 'orders':
        return renderOrdersSection();
      case 'wishlist':
        return <WishlistSection items={wishlistItems} onAddToCart={handleAddToCart} onRemoveFromWishlist={handleRemoveFromWishlist} />;
      case 'subscriptions':
        return <SubscriptionsSection subscriptions={subscriptions} onUnsubscribe={handleUnsubscribe} />;
      case 'notifications':
        return <NotificationsSection notifications={notifications} onPreferenceChange={handleNotificationPreferenceChange} />;
      case 'settings':
        return <SettingsSection />;
      default:
        return <ProfileSection />;
    }
  };

  // Render profile section
  const ProfileSection: React.FC = () => {
    const { user, updateUserProfile } = useAuth();
    const isExternalAuth = !!(user as any)?.auth_provider;

    // Initial form state mapping from API response
    const initialForm = React.useMemo(() => ({
      firstName: (user && (user.first_name || (user as any).firstName)) || '',
      lastName: (user && (user.last_name || (user as any).lastName)) || '',
      username: (user && (user.username || user.email)) || '',
      email: (user && (user.email || user.username)) || '',
      phone: (user && (user.phone || '')) || '',
      address: (user && (user.address || '')) || '',
      birthday: (user && (user.birthday || '')) || ''
    }), [user]);

    const [formData, setFormData] = React.useState(initialForm);
    const [isEditing, setIsEditing] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
      setFormData(initialForm);
    }, [initialForm]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      try {
        const changed: any = {};
        // Для внешних аккаунтов запрещаем менять имя/фамилию/email/адрес вообще
        if (!isExternalAuth) {
          if (formData.firstName !== initialForm.firstName) changed.first_name = formData.firstName;
          if (formData.lastName !== initialForm.lastName) changed.last_name = formData.lastName;
        }
        // username and email are read-only и не отправляются на изменение
        // Телефон и дата рождения:
        // - если внешний провайдер и поле было пустым, даём его заполнить;
        // - если уже не пустое, считаем, что пришло от провайдера и не меняем.
        if (!isExternalAuth || !initialForm.phone) {
          if (formData.phone !== initialForm.phone) changed.phone = formData.phone;
        }
        if (!isExternalAuth || !initialForm.birthday) {
          if (formData.birthday !== initialForm.birthday) changed.birthday = formData.birthday;
        }
        if (!isExternalAuth && formData.address !== initialForm.address) {
          changed.address = formData.address;
        }

        if (Object.keys(changed).length === 0) {
          // Nothing changed — just close editor
          setIsEditing(false);
          return;
        }

        // Call service to update only changed fields (PUT /users/profile/)
        const updated = await userServiceUpdate(changed as any);

        // Update auth context with returned data if available
        try {
          await updateUserProfile(updated as any);
        } catch (e) {
          // Fallback: if auth update fails, still proceed — user data may refresh elsewhere
          console.warn('Failed to update auth context after profile PUT', e);
        }

        addToast({ title: 'Успешно', description: 'Данные профиля обновлены', color: 'success' });
        setIsEditing(false);
      } catch (error) {
        console.error('Ошибка при обновлении профиля:', error);
        addToast({ title: 'Ошибка', description: (error?.response?.data?.message || 'Не удалось обновить профиль'), color: 'danger' });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        <Card className="border border-divider">
          <CardBody className="p-6">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-grow">
                <form onSubmit={handleSubmit} className="space-y-4 w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-medium">Личные данные</h3>
                    {!isEditing ? (
                      <Button
                        color="primary"
                        variant="light"
                        onPress={() => setIsEditing(true)}
                        startContent={<Icon icon="lucide:edit" width={16} />}
                      >
                        Редактировать
                      </Button>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Имя"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      disabled={!isEditing || isExternalAuth}
                    />
                    <Input
                      label="Фамилия"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      disabled={!isEditing || isExternalAuth}
                    />

                    <Input
                      label="Логин (username)"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      disabled // обычно не редактируется
                    />

                    <Input
                      label={(user && (user.is_active || (user as any).isActive)) ? 'Email (подтверждён)' : 'Email'}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled // смена почты обычно требует подтверждения
                      className={(user && (user.is_active || (user as any).isActive)) ? 'border-success bg-success/5' : undefined}
                      endContent={
                        (user && (user.is_active || (user as any).isActive)) ? (
                          <Icon icon="lucide:check" width={16} className="text-success" />
                        ) : null
                      }
                    />

                    <Input
                      label={
                        isExternalAuth && !!initialForm.phone
                          ? 'Телефон (подтверждён провайдером)'
                          : 'Телефон'
                      }
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!isEditing || (isExternalAuth && !!initialForm.phone)}
                      className={
                        isExternalAuth && !!initialForm.phone
                          ? 'border-success bg-success/5'
                          : undefined
                      }
                      endContent={
                        isExternalAuth && !!initialForm.phone ? (
                          <Icon icon="lucide:check" width={16} className="text-success" />
                        ) : null
                      }
                    />

                    <Input
                      label={
                        isExternalAuth && !!initialForm.birthday
                          ? 'Дата рождения (поле заполнено)'
                          : 'Дата рождения'
                      }
                      name="birthday"
                      type="date"
                      value={formData.birthday}
                      onChange={handleChange}
                      disabled={!isEditing || (isExternalAuth && !!initialForm.birthday)}
                      className={
                        isExternalAuth && !!initialForm.birthday
                          ? 'border-success bg-success/5'
                          : undefined
                      }
                    />
                  </div>

                  {isEditing && (
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        color="default"
                        variant="flat"
                        onPress={() => {
                          setIsEditing(false);
                          setFormData(initialForm);
                        }}
                      >
                        Отмена
                      </Button>
                      <Button color="primary" type="submit" isLoading={isLoading}>
                        Сохранить
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  // Render addresses section
  const renderAddressesSection = () => (
    <div className="space-y-8">
      {/* <h2 className="text-2xl font-light">Мои <span className="font-medium">адреса</span></h2> */}
      
      {isLoadingAddresses ? (
        // Loading skeleton
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i} className="border border-divider" disableRipple>
              <CardBody className="p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-content3/30 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-content3/30 rounded w-full mb-2"></div>
                  <div className="h-4 bg-content3/30 rounded w-4/5 mb-2"></div>
                  <div className="h-4 bg-content3/30 rounded w-2/3"></div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : userAddresses.length > 0 ? (
        <div className="space-y-4">
          {userAddresses.map((address) => (
            <AddressCard
              key={address.id}
              address={address}
              onEdit={(addr) => handleEditAddress(addr)}
              onDelete={(id) => handleDeleteAddress(id)}
              onSetDefault={(id) => handleSetDefaultAddress(id)}
            />
          ))}
          <Button
            variant="flat"
            color="primary"
            startContent={<Icon icon="lucide:plus" width={16} />}
            onPress={handleAddAddress}
          >
            Добавить адрес
          </Button>
        </div>
      ) : (
        // Empty state: no addresses — show centered message and Add button
        <div className="text-center py-12">
          <Icon icon="lucide:map-pin" className="mx-auto mb-4 text-foreground/40" width={48} />
          <h3 className="text-lg font-medium mb-2">Адресов не найдено</h3>
          <p className="text-foreground/70 mb-6">Добавьте адрес доставки, чтобы быстро оформлять заказы</p>
          <Button
            color="primary"
            variant="flat"
            startContent={<Icon icon="lucide:plus" width={16} />}
            onPress={handleAddAddress}
          >
            Добавить адрес
          </Button>
        </div>
      )}
      
      {/* Address add/edit modal */}
      <Modal
        isOpen={isAddressModalOpen}
        onClose={() => {
          onAddressModalClose();
          setCurrentAddress(null);
          setAddressForm({});
        }}
        backdrop="blur"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {currentAddress ? 'Редактировать адрес' : 'Добавить адрес'}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    label="Название"
                    placeholder="Например, Дом, Работа"
                    value={addressForm.title || ''}
                    onValueChange={(value) => setAddressForm({ ...(addressForm || {}), title: value })}
                  />

                  <div>
                    <label className="text-sm text-foreground/70 mb-2 block">Полный адрес</label>
                    <div ref={autocompletePortalRef} className="relative">
                      <AddressAutocomplete
                        placeholder="Город, улица, дом"
                        type="address"
                        restrict={addressForm.city}
                        disablePortal={true}
                        onSelect={(s: any) => {
                          const parsed = parseAddressSuggestion(s);
                          // Do NOT overwrite the user-provided "title" (Название).
                          // Populate only the address fields parsed from suggestion.
                          setAddressForm({
                            ...(addressForm || {}),
                            fullAddress: parsed.fullAddress || addressForm.fullAddress,
                            city: parsed.city || addressForm.city || '',
                            street: parsed.street || addressForm.street || '',
                            house: parsed.house || addressForm.house || '',
                            apartment: parsed.apartment || addressForm.apartment || ''
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Город"
                      value={addressForm.city || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), city: value })}
                    />
                    <Input
                      label="Улица"
                      value={addressForm.street || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), street: value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Дом"
                      value={addressForm.house || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), house: value })}
                    />
                    <Input
                      label="Квартира / Офис"
                      value={addressForm.apartment || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), apartment: value })}
                    />
                    {/* Postal code removed — not needed */}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Этаж"
                      value={addressForm.floor || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), floor: value })}
                    />
                    <Input
                      label="Домофон"
                      value={addressForm.intercom || ''}
                      onValueChange={(value) => setAddressForm({ ...(addressForm || {}), intercom: value })}
                    />
                    <div className="flex items-center">
                      <Checkbox
                        checked={!!addressForm.private_house}
                        onChange={(e: any) => setAddressForm({ ...(addressForm || {}), private_house: !!e.target?.checked })}
                      >
                        Частный дом
                      </Checkbox>
                    </div>
                  </div>

                  <Checkbox
                    checked={!!addressForm.isDefault}
                    onChange={(e: any) => setAddressForm({ ...(addressForm || {}), isDefault: !!e.target?.checked })}
                  >
                    Сделать основным
                  </Checkbox>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={() => {
                  onClose?.();
                  onAddressModalClose();
                  setCurrentAddress(null);
                  setAddressForm({});
                }}>
                  Отмена
                </Button>
                <Button color="primary" onPress={handleAddressSubmit}>
                  Сохранить
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );

  // Render loyalty section
  // const renderLoyaltySection = () => (
  //   <div className="space-y-8">
  //     <h2 className="text-2xl font-light">Мои <span className="font-medium">баллы</span></h2>
      
  //     {isLoadingLoyalty ? (
  //       // Loading skeleton
  //       <div className="space-y-6">
  //         <Card className="border border-divider" disableRipple>
  //           <CardBody className="p-6">
  //             <div className="animate-pulse">
  //               <div className="h-12 bg-content3/30 rounded w-1/3 mb-4"></div>
  //               <div className="h-4 bg-content3/30 rounded w-full mb-2"></div>
  //             </div>
  //           </CardBody>
  //         </Card>
          
  //         <div className="animate-pulse">
  //           <div className="h-6 bg-content3/30 rounded w-1/4 mb-4"></div>
  //           <div className="space-y-4">
  //             {[1, 2, 3, 4].map((i) => (
  //               <div key={i} className="h-12 bg-content3/30 rounded w-full"></div>
  //             ))}
  //           </div>
  //         </div>
  //       </div>
  //     ) : userLoyaltyData ? (
  //       // Actual content
  //       <div className="space-y-8">
  //         {/* Points Balance Card - Enhanced */}
  //         <Card className="border border-divider overflow-hidden" disableRipple>
  //           <div className="bg-primary/5 border-b border-divider">
  //             <CardBody className="py-8 px-6">
  //               <div className="flex flex-col md:flex-row items-center justify-between gap-6">
  //                 <div className="text-center md:text-left">
  //                   <p className="text-foreground/70 mb-2">Ваш текущий баланс</p>
  //                   <div className="flex items-center gap-3">
  //                     <h3 className="text-4xl font-semibold text-primary">
  //                       {userLoyaltyData.points}
  //                     </h3>
  //                     <span className="text-lg font-light">баллов</span>
  //                   </div>
  //                   <p className="mt-2 text-sm text-foreground/70">
  //                     ≈ {(userLoyaltyData.points / 10).toFixed(0)} ₽ для оплаты покупок
  //                   </p>
  //                 </div>
                  
  //                 <div className="flex-shrink-0 relative w-24 h-24 md:w-32 md:h-32">
  //                   <div className="absolute inset-0 rounded-full bg-primary/10 flex items-center justify-center">
  //                     <div className="relative w-16 h-16 md:w-24 md:h-24">
  //                       {/* Progress circle */}
  //                       <svg className="w-full h-full" viewBox="0 0 100 100">
  //                         <circle 
  //                           cx="50" cy="50" r="45" 
  //                           stroke="currentColor" 
  //                           strokeWidth="6"
  //                           fill="none"
  //                           className="text-content3"
  //                         />
  //                         <circle 
  //                           cx="50" cy="50" r="45" 
  //                           stroke="currentColor"
  //                           strokeWidth="6" 
  //                           strokeDasharray="282.7"
  //                           strokeDashoffset={(userLoyaltyData.nextLevelProgress / 100) * 282.7}
  //                           fill="none" 
  //                           className="text-primary transform -rotate-90 origin-center"
  //                         />
  //                       </svg>
  //                       <div className="absolute inset-0 flex flex-col items-center justify-center">
  //                         <span className="text-primary font-medium text-sm">{userLoyaltyData.nextLevelProgress}%</span>
  //                         <span className="text-xs text-foreground/60">до след.</span>
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </div>
                  
  //                 <div className="text-center md:text-left">
  //                   <div className="bg-primary/10 px-3 py-1 rounded-full inline-block mb-2">
  //                     <p className="text-xs font-medium text-primary">{userLoyaltyData.level}</p>
  //                   </div>
  //                   <p className="text-sm text-foreground/70 mb-1">Осталось <span className="font-medium">{userLoyaltyData.pointsToNextLevel}</span> баллов</p>
  //                   <p className="text-sm text-foreground/70">до уровня "{userLoyaltyData.nextLevel}"</p>
  //                 </div>
  //               </div>
  //             </CardBody>
  //           </div>
            
  //           <CardBody className="p-6">
  //             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
  //               <div>
  //                 <p className="text-foreground/70 mb-1 text-sm">Накоплено за все время</p>
  //                 <p className="font-medium text-lg">{userLoyaltyData.totalEarned} баллов</p>
  //               </div>
  //               <div>
  //                 <p className="text-foreground/70 mb-1 text-sm">Потрачено в этом месяце</p>
  //                 <p className="font-medium text-lg">{userLoyaltyData.spentThisMonth} баллов</p>
  //               </div>
  //               <div>
  //                 <p className="text-foreground/70 mb-1 text-sm">Срок действия</p>
  //                 <p className="font-medium text-lg">{userLoyaltyData.expirationDate}</p>
  //               </div>
  //             </div>
  //           </CardBody>
  //         </Card>

  //         {/* How to earn points section */}
  //         <Card className="border border-divider" disableRipple>
  //           <CardBody className="p-6">
  //             <h3 className="text-lg font-medium mb-6">Как заработать баллы</h3>
              
  //             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  //               {userLoyaltyData.earningOptions.map((option, index) => (
  //                 <div key={index} className="flex gap-4 items-start">
  //                   <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
  //                     <Icon icon={option.icon} className="text-primary" width={24} />
  //                   </div>
  //                   <div>
  //                     <h4 className="font-medium mb-1">{option.title}</h4>
  //                     <p className="text-sm text-foreground/70 mb-1">{option.description}</p>
  //                     <p className="text-primary font-medium text-sm">+{option.points} баллов</p>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
              
  //             <div className="mt-6 text-center">
  //               <Button 
  //                 color="primary" 
  //                 size="lg"
  //                 as="a"
  //                 href="#catalog"
  //                 endContent={<Icon icon="lucide:arrow-right" width={16} />}
  //               >
  //                 Перейти в каталог
  //               </Button>
  //             </div>
  //           </CardBody>
  //         </Card>

  //         {/* Rules accordion */}
  //         <Card className="border border-divider" disableRipple>
  //           <CardBody className="p-6">
  //             <h3 className="text-lg font-medium mb-6">Правила бонусной программы</h3>
              
  //             <Accordion variant="bordered">
  //               <AccordionItem key="1" aria-label="Общие положения" title="Общие положения">
  //                 <div className="space-y-2 text-foreground/80 text-sm">
  //                   <p>
  //                     Бонусная программа Aroma Rewards действует для всех зарегистрированных клиентов магазина.
  //                     Баллы начисляются за совершение покупок и активности в рамках программы.
  //                   </p>
  //                   <p>
  //                     Клиенты могут использовать накопленные баллы для получения скидки на последующие покупки.
  //                     1 балл = 0.1 рубля скидки при оплате заказа баллами.
  //                   </p>
  //                 </div>
  //               </AccordionItem>
                
  //               <AccordionItem key="2" aria-label="Начисление баллов" title="Начисление баллов">
  //                 <div className="space-y-2 text-foreground/80 text-sm">
  //                   <p>
  //                     <span className="font-medium">За покупки:</span> 5% от суммы заказа в баллами (1 рубль = 0.05 баллов)
  //                   </p>
  //                   <p>
  //                     <span className="font-medium">Отзыв о товаре:</span> 50 баллов за опубликованный отзыв с фото
  //                   </p>
  //                   <p>
  //                     <span className="font-medium">День рождения:</span> 500 баллов в день рождения клиента
  //                   </p>
  //                   <p>
  //                     <span className="font-medium">Первый заказ:</span> 300 баллов за первый заказ от 2000 рублей
  //                   </p>
  //                   <p>
  //                     <span className="font-medium">Регистрация:</span> 100 баллов за регистрацию на сайте
  //                   </p>
  //                 </div>
  //               </AccordionItem>
                
  //               <AccordionItem key="3" aria-label="Использование баллов" title="Использование баллов">
  //                 <div className="space-y-2 text-foreground/80 text-sm">
  //                   <p>
  //                     Баллы можно использовать для частичной или полной оплаты заказов.
  //                     Максимальная сумма оплаты баллами — 50% от стоимости заказа.
  //                   </p>
  //                   <p>
  //                     Для использования баллов необходимо авторизоваться на сайте и выбрать соответствующий способ оплаты при оформлении заказа.
  //                   </p>
  //                   <p>
  //                     Баллы нельзя использовать для оплаты доставки и товаров, участвующих в акциях.
  //                   </p>
  //                 </div>
  //               </AccordionItem>
                
  //               <AccordionItem key="4" aria-label="Уровни лояльности" title="Уровни лояльности">
  //                 <div className="space-y-4 text-foreground/80 text-sm">
  //                   <div>
  //                     <p className="font-medium">Базовый</p>
  //                     <p>0-3000 баллов</p>
  //                     <p>5% от суммы заказа возвращается баллами</p>
  //                   </div>
  //                   <div>
  //                     <p className="font-medium">Серебряный</p>
  //                     <p>3001-10000 баллов</p>
  //                     <p>7% от суммы заказа возвращается баллами</p>
  //                     <p>Доступ к эксклюзивным коллекциям</p>
  //                   </div>
  //                   <div>
  //                     <p className="font-medium">Золотой</p>
  //                     <p>10001+ баллов</p>
  //                     <p>10% от суммы заказа возвращается баллами</p>
  //                     <p>Доступ к эксклюзивным коллекциям</p>
  //                     <p>Бесплатная доставка</p>
  //                   </div>
  //                 </div>
  //               </AccordionItem>
                
  //               <AccordionItem key="5" aria-label="Срок действия баллов" title="Срок действия баллов">
  //                 <div className="space-y-2 text-foreground/80 text-sm">
  //                   <p>
  //                     Срок действия баллов — 12 месяцев с момента начисления.
  //                   </p>
  //                   <p>
  //                     За 30 дней до истечения срока действия баллов мы отправим вам уведомление 
  //                     по email и в личном кабинете.
  //                   </p>
  //                   <p>
  //                     Израсходуйте баллы до истечения их срока действия, чтобы не потерять их.
  //                   </p>
  //                 </div>
  //               </AccordionItem>
  //             </Accordion>
  //           </CardBody>
  //         </Card>
          
  //         {/* History of points */}
  //         <div>
  //           <div className="flex justify-between items-center mb-4">
  //             <h3 className="text-lg font-medium">История баллов</h3>
  //             <div className="flex gap-2">
  //               <Button 
  //                 size="sm" 
  //                 variant="flat" 
  //                 color="default"
  //                 className={userLoyaltyData.historyFilter === 'all' ? 'bg-content2' : ''}
  //                 onPress={() => {
  //                   // В проде: раскомментировать
  //                   // setHistoryFilter('all');
  //                   // fetchLoyaltyHistory('all');
  //                 }}
  //               >
  //                 Все
  //               </Button>
  //               <Button 
  //                 size="sm" 
  //                 variant="flat" 
  //                 color="default"
  //                 className={userLoyaltyData.historyFilter === 'earned' ? 'bg-content2' : ''}
  //                 onPress={() => {
  //                   // В проде: раскомментировать
  //                   // setHistoryFilter('earned');
  //                   // fetchLoyaltyHistory('earned');
  //                 }}
  //               >
  //                 Начисления
  //               </Button>
  //               <Button 
  //                 size="sm" 
  //                 variant="flat" 
  //                 color="default"
  //                 className={userLoyaltyData.historyFilter === 'spent' ? 'bg-content2' : ''}
  //                 onPress={() => {
  //                   // В проде: раскомментировать
  //                   // setHistoryFilter('spent');
  //                   // fetchLoyaltyHistory('spent');
  //                 }}
  //               >
  //                 Списания
  //               </Button>
  //             </div>
  //           </div>
            
  //           <Card className="border border-divider">
  //             <CardBody className="p-0">
  //               <Table
  //                 aria-label="История баллов"
  //                 removeWrapper
  //                 className="min-w-full"
  //               >
  //                 <TableHeader>
  //                   <TableColumn>ДАТА</TableColumn>
  //                   <TableColumn>ОПЕРАЦИЯ</TableColumn>
  //                   <TableColumn>БАЛЛЫ</TableColumn>
  //                   <TableColumn>СТАТУС</TableColumn>
  //                 </TableHeader>
  //                 <TableBody>
  //                   {userLoyaltyData.history.map((item, index) => (
  //                     <TableRow key={index}>
  //                       <TableCell>{item.date}</TableCell>
  //                       <TableCell>
  //                         <div>
  //                           <p className="font-medium">{item.description}</p>
  //                           {item.orderNumber && (
  //                             <p className="text-xs text-foreground/60">Заказ #{item.orderNumber}</p>
  //                           )}
  //                         </div>
  //                       </TableCell>
  //                       <TableCell>
  //                         <span className={`font-medium ${item.points > 0 ? 'text-success' : 'text-danger'}`}>
  //                           {item.points > 0 ? '+' : ''}{item.points}
  //                         </span>
  //                       </TableCell>
  //                       <TableCell>
  //                         <Chip 
  //                           size="sm" 
  //                           variant="flat" 
  //                           color={item.status === 'processed' ? 'success' : item.status === 'pending' ? 'warning' : 'default'}
  //                         >
  //                           {item.status === 'processed' ? 'Обработано' : 
  //                             item.status === 'pending' ? 'В обработке' : 'Истекло'}
  //                         </Chip>
  //                       </TableCell>
  //                     </TableRow>
  //                   ))}
  //                 </TableBody>
  //               </Table>
  //             </CardBody>
  //           </Card>
            
  //           {userLoyaltyData.history.length > 5 && (
  //             <div className="flex justify-center mt-4">
  //               <Button 
  //                 variant="flat" 
  //                 color="default"
  //                 endContent={<Icon icon="lucide:chevron-down" width={16} />}
  //                 onPress={() => {
  //                   // В проде: раскомментировать
  //                   // loadMoreHistory();
  //                 }}
  //               >
  //                 Загрузить еще
  //               </Button>
  //             </div>
  //           )}
  //         </div>
  //       </div>
  //     ) : (
  //       <p>Не удалось загрузить данные по баллам.</p>
  //     )}
  //   </div>
  // );

  // Render orders section
  const renderOrdersSection = () => {
    const formatDate = (dateString?: string) => {
      if (!dateString) return '—';
      const parsed = Date.parse(dateString);
      if (Number.isNaN(parsed)) return '—';
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).format(new Date(parsed));
    };

    const formatCurrency = (value?: number) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return '0 ₽';
      return `${value.toLocaleString('ru-RU')} ₽`;
    };

    const normalizeStatus = (status: Order['status']) => (status || 'processing').toLowerCase() as Order['status'];

    const getOrderDisplayNumber = (order: Order) => {
      const raw = (order as any).order_id ?? order.orderId ?? order.id;
      return raw || order.id;
    };

    const getStatusPillColor = (status: Order['status']) => {
      switch (normalizeStatus(status)) {
        case 'processing':
          return 'primary';
        case 'shipped':
          return 'secondary';
        case 'delivered':
        case 'completed':
          return 'success';
        case 'cancelled':
          return 'danger';
        default:
          return 'default';
      }
    };

    const getStatusLabel = (status: Order['status']) => {
      switch (normalizeStatus(status)) {
        case 'processing':
          return 'В обработке';
        case 'shipped':
          return 'В пути';
        case 'delivered':
        case 'completed':
          return 'Доставлен';
        case 'cancelled':
          return 'Отменён';
        default:
          return status;
      }
    };

    const getDeliveryCarrierLabel = (type?: string | null) => {
      if (!type) return null;
      const normalized = String(type).trim().toLowerCase();
      if (!normalized) return null;
      const labels: Record<string, string> = {
        cdek: 'СДЭК'
      };
      return labels[normalized] || type;
    };

    const getDeliveryDisplay = (order: Order, fallbackText = 'Доставка уточняется') => {
      const carrier = getDeliveryCarrierLabel(order.deliveryType);
      return carrier || fallbackText;
    };

    const summary = userOrders.reduce(
      (acc, order) => {
        const status = normalizeStatus(order.status);
        if (status === 'processing') acc.active += 1;
        if (status === 'shipped') acc.transit += 1;
        if (status === 'delivered' || status === 'completed') acc.delivered += 1;
        return acc;
      },
      { active: 0, transit: 0, delivered: 0 }
    );

    const groupsMap = new Map<string, { label: string; sortValue: number; items: Order[] }>();
    userOrders.forEach((order) => {
      const parsed = Date.parse(order.date);
      const date = Number.isNaN(parsed) ? null : new Date(parsed);
      const key = date ? `${date.getFullYear()}-${date.getMonth()}` : 'other';
      const label = date ? date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }) : 'Без даты';
      const entry = groupsMap.get(key);
      if (entry) {
        entry.items.push(order);
      } else {
        groupsMap.set(key, {
          label,
          items: [order],
          sortValue: date ? date.getTime() : 0
        });
      }
    });

    const groupedOrders = Array.from(groupsMap.values())
      .sort((a, b) => b.sortValue - a.sortValue)
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
      }));

    const selectedOrder = (() => {
      if (!userSelectedOrder) return null;
      if (typeof userSelectedOrder === 'object') {
        return userSelectedOrder as Order;
      }
      return userOrders.find((order) => String(order.id) === String(userSelectedOrder)) || null;
    })();

    const renderTimeline = (status: Order['status']) => {
      const steps = [
        { key: 'created', label: 'Создан' },
        { key: 'packed', label: 'Укомплектован' },
        { key: 'shipped', label: 'Передан доставке' },
        { key: 'delivered', label: 'Получен' }
      ];
      const progressMap: Record<string, number> = {
        processing: 1,
        shipped: 2,
        delivered: 3,
        completed: 3,
        cancelled: 1
      };
      const activeIdx = progressMap[normalizeStatus(status)] ?? 0;

      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {steps.map((step, idx) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      idx <= activeIdx ? 'bg-primary text-white' : 'bg-content2 text-foreground/60'
                    }`}
                  >
                    {idx + 1}
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-px ${idx < activeIdx ? 'bg-primary' : 'bg-divider'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-xs text-foreground/60">
            {steps.map((step) => (
              <span key={step.key}>{step.label}</span>
            ))}
          </div>
        </div>
      );
    };

    if (isLoadingOrders && !userOrders.length) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((skeleton) => (
            <Card key={skeleton} className="border border-divider">
              <CardBody className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-content3/30 rounded w-1/3" />
                  <div className="h-4 bg-content3/30 rounded w-full" />
                  <div className="h-4 bg-content3/30 rounded w-2/3" />
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      );
    }

    if (!userOrders.length) {
      return (
        <Card className="border border-divider">
          <CardBody className="py-16 flex flex-col items-center justify-center">
            <div className="bg-content2/50 p-4 rounded-full mb-4">
              <Icon icon="lucide:shopping-bag" className="text-foreground/50" width={32} />
            </div>
            <h3 className="text-xl font-medium mb-2">У вас пока нет заказов</h3>
            <p className="text-foreground/70 mb-6 text-center">
              Перейдите в каталог, чтобы сделать первый заказ
            </p>
            <Button color="primary" as="a" href="/catalog">
              Перейти в каталог
            </Button>
          </CardBody>
        </Card>
      );
    }

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Активные', value: summary.active, icon: 'lucide:loader' },
            { label: 'В пути', value: summary.transit, icon: 'lucide:truck' },
              { label: 'Доставлены', value: summary.delivered, icon: 'lucide:check-circle' }
          ].map((card) => (
            <Card key={card.label} className="border border-divider" disableRipple>
              <CardBody className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-content2 flex items-center justify-center">
                  <Icon icon={card.icon} className="text-foreground/70" width={22} />
                </div>
                <div>
                  <p className="text-sm text-foreground/60">{card.label}</p>
                  <p className="text-2xl font-semibold">{card.value}</p>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        <div className="space-y-10">
          {groupedOrders.length === 0 ? (
            <div className="text-center py-12 text-foreground/70">История заказов пуста</div>
          ) : (
            groupedOrders.map((group) => (
              <div key={group.label} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium capitalize">{group.label}</h3>
                  <div className="h-px flex-1 ml-6 bg-gradient-to-r from-divider via-divider/70 to-transparent" />
                </div>
                <div className="space-y-4">
                  {group.items.map((order) => {
                    const orderTotal = order.total ?? (order.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0);
                    const previewItems = order.items?.slice(0, 3) || [];
                    const remaining = Math.max((order.items?.length || 0) - previewItems.length, 0);
                    const eta = (order as any).deliveryDate || (order as any).estimated_delivery || null;

                    return (
                      <Card key={order.id} className="border border-divider" disableRipple>
                        <CardBody className="p-6 space-y-6">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                            <div className="flex-1 space-y-4">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                <div>
                                  <p className="text-sm uppercase tracking-[0.2em] text-foreground/50">Заказ #{getOrderDisplayNumber(order)}</p>
                                  <h4 className="text-xl font-medium mt-1">{formatCurrency(orderTotal)}</h4>
                                  <p className="text-sm text-foreground/60">{formatDate(order.date)} · {order.items?.length ?? 0} позиций</p>
                                </div>
                                <div className="flex flex-col items-start sm:items-end gap-2">
                                  <Chip size="sm" color={getStatusPillColor(order.status)} variant="flat">
                                    {getStatusLabel(order.status)}
                                  </Chip>
                                  {eta && <p className="text-xs text-foreground/60">Ориентировочно: {formatDate(eta)}</p>}
                                </div>
                              </div>

                              {renderTimeline(order.status)}

                              <div className="flex flex-wrap items-center gap-3">
                                {previewItems.map((item) => (
                                  <div key={item.id} className="flex items-center gap-2 bg-content2/60 rounded-full px-3 py-1">
                                    <span className="text-xs font-medium text-foreground/50">×{item.quantity}</span>
                                    <span className="text-sm text-foreground/80">{item.name}</span>
                                  </div>
                                ))}
                                {remaining > 0 && (
                                  <span className="text-sm text-foreground/60">+ ещё {remaining}</span>
                                )}
                              </div>
                            </div>

                            <div className="w-full lg:w-60 flex flex-col gap-3">
                              <div className="bg-content2/40 rounded-2xl p-4">
                                <p className="text-xs text-foreground/50 uppercase tracking-[0.2em] mb-1">Доставка</p>
                                <p className="text-sm font-medium text-foreground/90">
                                  {getDeliveryDisplay(order)}
                                </p>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <Button variant="flat" color="default" onPress={() => setUserSelectedOrder(order.id)}>
                                  Подробнее
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <AnimatePresence>
          {selectedOrder && (
            <motion.div
              key={selectedOrder.id}
              className="fixed inset-0 bg-background/70 backdrop-blur-sm flex justify-end z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  setUserSelectedOrder(null);
                }
              }}
            >
              <motion.div
                className="bg-content1 w-full max-w-xl h-full shadow-2xl overflow-auto"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="p-6 space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-foreground/50">Заказ #{getOrderDisplayNumber(selectedOrder)}</p>
                      <h3 className="text-2xl font-semibold mt-1">{formatCurrency(selectedOrder.total)}</h3>
                      <p className="text-sm text-foreground/60">от {formatDate(selectedOrder.date)}</p>
                    </div>
                    <Button isIconOnly variant="light" onPress={() => setUserSelectedOrder(null)}>
                      <Icon icon="lucide:x" width={20} />
                    </Button>
                  </div>

                  <Divider />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-foreground/50 uppercase tracking-[0.2em]">Статус</p>
                      <Chip className="mt-2" color={getStatusPillColor(selectedOrder.status)} variant="flat">
                        {getStatusLabel(selectedOrder.status)}
                      </Chip>
                    </div>
                    <div>
                      <p className="text-xs text-foreground/50 uppercase tracking-[0.2em]">Оплата</p>
                      <p className="mt-2 font-medium text-foreground/90">{(selectedOrder as any).paymentMethod || 'Банковская карта'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground/50 uppercase tracking-[0.2em]">Доставка</p>
                      <p className="mt-2 font-medium text-foreground/90">{getDeliveryDisplay(selectedOrder, 'Доставка уточняется')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground/50 uppercase tracking-[0.2em]">Промокод</p>
                      <p className="mt-2 font-medium text-foreground/90">{(selectedOrder as any).promoCode || '—'}</p>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h4 className="text-lg font-medium mb-3">Состав заказа</h4>
                    <div className="space-y-4">
                      {selectedOrder.items.map((item) => (
                        <div key={item.id} className="flex gap-3">
                          <div className="w-12 h-12 rounded-full bg-content2/60 flex-shrink-0 flex items-center justify-center text-sm font-semibold text-foreground/60">
                            ×{item.quantity}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{item.name}</p>
                            <div className="flex justify-between text-sm text-foreground/70 mt-1">
                              <span>{formatCurrency(item.price)} × {item.quantity}</span>
                              <span>{formatCurrency(item.price * item.quantity)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col gap-2">
                    {selectedOrder.status === 'processing' && (
                      <Button color="danger" variant="flat" onPress={() => handleCancelOrder(String(selectedOrder.id))}>
                        Отменить заказ
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    );
  };

  // Render wishlist section - Update this to use the hook's handleRemoveFromWishlist
  const renderWishlistSection = () => (
    <div>
      <h2 className="text-xl font-medium mb-6">Избранное</h2>
      
      {userWishlistItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {userWishlistItems.map(item => (
            <Card key={item.id} className="border border-divider product-card-hover">
              <CardBody className="p-0 overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <Divider />
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-sm text-foreground/70">{item.category}</p>
                    </div>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      onPress={() => handleRemoveFromWishlist(item.id)}
                    >
                      <Icon icon="lucide:x" width={14} />
                    </Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="font-medium">{item.price} ₽</p>
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      startContent={<Icon icon="lucide:shopping-bag" width={14} />}
                    >
                      В корзину
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon icon="lucide:heart-off" className="mx-auto mb-4 text-foreground/40" width={48} />
          <h3 className="text-lg font-medium mb-2">В избранном пока ничего нет</h3>
          <p className="text-foreground/70 mb-6">Добавляйте товары в избранное, чтобы не потерять их из виду</p>
          <Button
            color="primary"
            variant="flat"
            as="a"
            href="#catalog"
          >
            Перейти в каталог
          </Button>
        </div>
      )}
    </div>
  );

  // Render recommendations section
  const renderRecommendationsSection = () => (
    <div>
      <h2 className="text-xl font-medium mb-6">Персональные рекомендации</h2>
      
      {userRecommendations.length > 0 ? (
        <div className="space-y-6">
          {userRecommendations.map(item => (
            <Card key={item.id} className="border border-divider">
              <CardBody className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="sm:w-40 h-40 bg-content2 flex-shrink-0">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-grow flex flex-col justify-between">
                    <div className="mb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{item.name}</h3>
                          <p className="text-sm text-foreground/70">{item.category}</p>
                        </div>
                        <p className="font-medium">{item.price} ₽</p>
                      </div>
                      <p className="text-sm text-foreground/70 mt-2">{item.reason}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-auto">
                      <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        startContent={<Icon icon="lucide:shopping-bag" width={14} />}
                      >
                        В корзину
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        startContent={<Icon icon="lucide:heart" width={14} />}
                      >
                        В избранное
                      </Button>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon icon="lucide:sparkles" className="mx-auto mb-4 text-foreground/40" width={48} />
          <h3 className="text-lg font-medium mb-2">Рекомендации появятся скоро</h3>
          <p className="text-foreground/70 mb-6">Совершайте покупки, чтобы мы могли предложить вам подходящие товары</p>
          <Button
            color="primary"
            variant="flat"
            as="a"
            href="#catalog"
          >
            Перейти в каталог
          </Button>
        </div>
      )}
    </div>
  );

  // Render security section
  const renderSecuritySection = () => (
    <div>
      {/* <h2 className="text-xl font-medium mb-6">Безопасность</h2> */}

      {user && (user as any).auth_provider ? (
        <Card className="border border-divider bg-content2/50 mb-6">
          <CardBody className="p-6 flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FC3F1D] text-white text-lg font-semibold flex-shrink-0">
              Я
            </div>
            <div className="flex flex-col gap-1 flex-grow">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-medium">Авторизованы через Яндекс ID</h3>
                <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
                  <Icon icon="lucide:check" width={14} />
                  Подтверждено
                </span>
              </div>
              <p className="text-foreground/70 text-sm">
                Вы авторизованы с использованием Яндекс ID. Управление паролем выполняется в аккаунте Яндекса,
                поэтому смена пароля на сайте SAFONOVA недоступна.
              </p>
              <p className="text-foreground/60 text-xs mt-1">
                Если хотите войти другим способом,{' '}
                <button
                  type="button"
                  onClick={logout}
                  className="text-primary hover:underline"
                >
                  выйдите из аккаунта
                </button>{' '}
                и выберите другой вариант авторизации.
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="border border-divider bg-content2/50 mb-6">
          <CardBody className="p-6">
            <h3 className="text-lg font-medium mb-4">Смена пароля</h3>
            <div className="space-y-4">
              <p className="text-foreground/70 text-sm">
                Для обеспечения безопасности вашего аккаунта рекомендуется менять пароль не реже одного раза в 3 месяца
              </p>
              <Button
                color="primary"
                variant="flat"
                startContent={<Icon icon="lucide:lock" width={16} />}
                onPress={onPasswordModalOpen}
              >
                Изменить пароль
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
      
      {/* <Card className="border border-divider bg-content2/50">
        <CardBody className="p-6">
          <h3 className="text-lg font-medium mb-4">Настройки безопасности</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Двухфакторная аутентификация</p>
                <p className="text-sm text-foreground/70">
                  Дополнительный уровень защиты для вашего аккаунта
                </p>
              </div>
              <Button
                color="primary"
                variant="light"
              >
                Подключить
              </Button>
            </div>
            <Divider />
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">История входов</p>
                <p className="text-sm text-foreground/70">
                  Проверьте историю входов в ваш аккаунт
                </p>
              </div>
              <Button
                color="primary"
                variant="light"
              >
                Просмотреть
              </Button>
            </div>
          </div>
        </CardBody>
      </Card> */}

      {/* Password Change Modal */}
      <Modal 
        isOpen={isPasswordModalOpen} 
        onClose={onPasswordModalClose}
        backdrop="blur"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Изменение пароля
              </ModalHeader>
              <ModalBody>
                <div className="space-y-6">
                  <Input
                    type="password"
                    label="Текущий пароль"
                    placeholder="Введите текущий пароль"
                    variant="bordered"
                    isRequired
                    value={passwordData.currentPassword}
                    onValueChange={(value) => setPasswordData({...passwordData, currentPassword: value})}
                    isInvalid={!!formErrors.currentPassword}
                    errorMessage={formErrors.currentPassword}
                  />
                  <Input
                    type="password"
                    label="Новый пароль"
                    placeholder="Минимум 8 символов"
                    variant="bordered"
                    isRequired
                    value={passwordData.newPassword}
                    onValueChange={(value) => setPasswordData({...passwordData, newPassword: value})}
                    isInvalid={!!formErrors.newPassword}
                    errorMessage={formErrors.newPassword}
                  />
                  <Input
                    type="password"
                    label="Подтверждение пароля"
                    placeholder="Повторите новый пароль"
                    variant="bordered"
                    isRequired
                    value={passwordData.confirmPassword}
                    onValueChange={(value) => setPasswordData({...passwordData, confirmPassword: value})}
                    isInvalid={!!formErrors.confirmPassword}
                    errorMessage={formErrors.confirmPassword}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Отмена
                </Button>
                <Button color="primary" onPress={handlePasswordChange}>
                  Изменить пароль
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );

  // Render promotions section
  const renderPromotionsSection = () => (
    <div>
      <h2 className="text-xl font-medium mb-6">Персональные акции</h2>
      
      {userPromotions.length > 0 ? (
        <div className="space-y-6">
          {userPromotions.map(promo => (
            <Card key={promo.id} className="border border-divider border-l-4 border-l-primary">
              <CardBody className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium">{promo.title}</h3>
                  <Badge variant="flat" color="primary">{promo.discount}%</Badge>
                </div>
                <p className="text-foreground/70 mb-4">{promo.description}</p>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="bg-content2/50 px-4 py-2 rounded flex items-center">
                    <span className="font-mono font-medium mr-2">{promo.code}</span>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => navigator.clipboard.writeText(promo.code)}
                    >
                      <Icon icon="lucide:copy" width={14} />
                    </Button>
                  </div>
                  <p className="text-sm text-foreground/70">
                    Действует до: <span className="font-medium">{promo.expiryDate}</span>
                  </p>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon icon="lucide:ticket" className="mx-auto mb-4 text-foreground/40" width={48} />
          <h3 className="text-lg font-medium mb-2">У вас пока нет персональных акций</h3>
          <p className="text-foreground/70 mb-6">Продолжайте совершать покупки, чтобы получать специальные предложения</p>
          <Button
            color="primary"
            variant="flat"
            as="a"
            href="#catalog"
          >
            Перейти в каталог
          </Button>
        </div>
      )}
    </div>
  );


  return (
    <div className="space-y-6 py-6 md:py-12">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Desktop sidebar navigation */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 space-y-3">
              <Card className="border border-divider">
                <CardBody className="p-0">
                  <div className="p-6 border-b border-divider">
                    <div className="flex items-center gap-4">
                      {/* Avatar intentionally removed per UX request */}
                      <div>
                        <h3 className="font-medium">{(user?.first_name || user?.firstName) ? `${user?.first_name || user?.firstName} ${user?.last_name || user?.lastName || ''}` : (user?.name || "Пользователь")}</h3>
                        <p className="text-sm text-foreground/70">{user?.email || "example@mail.com"}</p>
                      </div>
                    </div>
                  </div>
                  <ul>
                    {menuItems.map((item) => (
                      <li key={item.key}>
                        <Button
                          variant={activeSection === item.key ? "solid" : "light"}
                          color={activeSection === item.key ? "primary" : "default"}
                          className="w-full justify-start rounded-none"
                          onPress={() => onSectionChange(item.key)}
                          startContent={item.icon}
                        >
                          {item.label}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
              <Button
                variant="bordered"
                color="danger"
                className="w-full justify-start"
                startContent={<Icon icon="lucide:log-out" width={18} />}
                onPress={() => logout()}
              >
                Выход
              </Button>
            </div>
          </div>

          {/* Mobile navigation - handled by renderMobileMenu() */}
          {renderMobileMenu()}

          {/* Main content area */}
          <div className="lg:col-span-9">
            <motion.div
              key={activeSection}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={pageVariants}
            >
              <div className="space-y-6">
                {/* Section header */}
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-light flex items-center">
                    <span className="inline-flex items-center">{getSectionIcon(activeSection)}</span>
                    <span className="ml-3">{getSectionTitle(activeSection)}</span>
                  </h1>
                </div>

                {/* Section content */}
                {activeSection === "profile" && <ProfileSection />}
                {activeSection === "addresses" && renderAddressesSection()}
                {activeSection === "loyalty" && renderLoyaltySection()}
                {activeSection === "orders" && renderOrdersSection()}
                {activeSection === "wishlist" && renderWishlistSection()}
                {activeSection === "reviews" && <ReviewsSection />}
                {activeSection === "recommendations" && renderRecommendationsSection()}
                {activeSection === "security" && renderSecuritySection()}
                {activeSection === "promotions" && renderPromotionsSection()}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;